import { useEffect, useRef, useState } from "react";
import { Captions, Loader2, AlertTriangle, Info, Maximize2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CaptionFullscreen } from "./CaptionFullscreen";
import { TypedText, useTypedLines } from "./typewriter";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtTime } from "@/lib/api";
import { useApp } from "@/lib/i18n";

const LIVE_LANGS = [["ms-MY", "Bahasa Melayu"], ["en-MY", "English (Malaysia)"], ["en-US", "English (US)"]];

function useStickToBottom(dep) {
  const ref = useRef(null);
  const stick = useRef(true);
  useEffect(() => {
    const el = ref.current;
    if (el && stick.current) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [dep]);
  const onScroll = () => { const el = ref.current; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; };
  return [ref, onScroll];
}

const ERRORS = {
  denied: ["Pengecaman pertuturan pelayar disekat. Benarkan mikrofon untuk laman ini.", "Browser speech recognition is blocked. Allow the microphone for this site."],
  network: ["Sambungan transkripsi langsung terganggu — mencuba semula… Rakaman audio tidak terjejas.", "Live transcription connection interrupted — retrying… Audio recording is unaffected."],
  start: ["Transkripsi langsung tidak dapat dimulakan. Rakaman audio tetap berjalan.", "Live transcription could not start. Audio recording continues."],
  plan: ["Transkripsi langsung untuk audio sistem/tab tersedia pada pelan berbayar. Rakaman audio tetap berjalan dan akan ditranskrip selepas dihantar.", "Live transcription for system/tab audio is on Pro. Recording continues and will be transcribed after sending."],
};

export const LiveTranscriptPanel = ({ live, state, elapsed }) => {
  const { L } = useApp();
  const [full, setFull] = useState(false);
  const { lines, interim, pending, error, lang, setLang, engine } = live;
  const words = lines.reduce((n, l) => n + l.text.split(/\s+/).filter(Boolean).length, 0) + (interim ? interim.split(/\s+/).length : 0);
  const { typed, typing, shown } = useTypedLines(lines, interim);
  const [box, onScroll] = useStickToBottom(shown);
  const active = state === "recording";
  return (
    <>
    {full && <CaptionFullscreen live={live} state={state} elapsed={elapsed} onClose={() => setFull(false)} />}
    <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="live-transcript-panel">
      <header className="flex flex-wrap items-center gap-3 border-b border-slate-100 bg-slate-50/70 px-5 py-3">
        <span className="flex items-center gap-2 font-heading text-sm font-bold text-slate-900"><Captions size={17} className="text-violet-600" /> {L("Transkripsi langsung", "Live transcription")}</span>
        {active && <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-red-600" data-testid="live-indicator"><span className="rec-pulse h-1.5 w-1.5 rounded-full bg-red-500" /> {L("Langsung", "Live")}</span>}
        <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-[11px] font-semibold text-violet-700" data-testid="live-engine-badge">{engine === "browser" ? L("Pelayar · serta-merta", "Browser · instant") : L("Whisper · setiap ~6 saat", "Whisper · every ~6s")}</span>
        {pending > 0 && <span className="flex items-center gap-1 text-xs text-slate-500" data-testid="live-pending"><Loader2 size={12} className="animate-spin" /> {L("Menukar audio…", "Converting audio…")}</span>}
        <div className="ml-auto flex flex-wrap items-center gap-2 sm:gap-3">
          <span className="text-xs font-medium text-slate-500" data-testid="live-word-count">{words.toLocaleString()} {L("perkataan", "words")}</span>
          <Button size="sm" variant="outline" className="h-8 rounded-lg bg-white text-xs" onClick={() => setFull(true)} data-testid="caption-fullscreen-btn"><Maximize2 size={13} className="mr-1" /> {L("Skrin penuh", "Full screen")}</Button>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="h-8 w-[150px] sm:w-[170px] rounded-lg bg-white text-xs" data-testid="live-language-select" aria-label={L("Bahasa transkripsi langsung", "Live transcription language")}><SelectValue /></SelectTrigger>
            <SelectContent>{LIVE_LANGS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`live-lang-${v}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </header>
      {error && <p className="flex items-center gap-2 bg-amber-50 px-5 py-2 text-xs text-amber-800" data-testid="live-error"><AlertTriangle size={14} /> {L(...ERRORS[error])}</p>}
      <div ref={box} onScroll={onScroll} className="slim-scroll h-[360px] overflow-y-auto px-6 py-6 sm:h-[460px] sm:px-10" data-testid="live-transcript-text" aria-live="polite">
        {!lines.length && !interim ? (
          <div className="grid h-full place-items-center text-center">
            <p className="max-w-md text-lg font-medium text-slate-400">
              {active ? L("Sedang mendengar… mula bercakap.", "Listening… start speaking.") : state === "paused" ? L("Rakaman dijeda.", "Recording paused.") : L("Tekan “Mula rakam” dan mula bercakap — setiap perkataan akan muncul di sini secara langsung.", "Press “Start recording” and speak — every word appears here live.")}
            </p>
          </div>
        ) : (
          <div className="space-y-4 text-xl font-medium leading-relaxed text-slate-900 sm:text-[26px] sm:leading-[1.6]">
            {typed.map((it, i) => (
              <p key={it.key} className="flex gap-4" data-testid={it.final ? `live-line-${it.id}` : "live-interim"}>
                <span className="w-12 shrink-0 pt-1.5 font-mono text-xs font-normal text-violet-500 sm:pt-2.5">{it.final ? fmtTime(it.t) : ""}</span>
                <span className={`min-w-0 flex-1 ${it.final ? "live-final" : "text-slate-400"}`}>
                  <TypedText text={it.typed} />{i === typed.length - 1 && (active || typing) && <span className="live-caret" />}
                </span>
              </p>
            ))}
          </div>
        )}
      </div>
      <footer className="flex items-center gap-2 border-t border-slate-100 px-5 py-2.5 text-xs text-slate-500">
        <Info size={13} /> {L("Pratonton sahaja. Transkrip akhir diproses selepas dihantar dengan pengecaman suara pembicara dan pemulihan AI.", "Preview only. The final transcript is produced after sending, with voice speaker detection and AI polish.")}
      </footer>
    </section>
    </>
  );
};
